import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import MessageForm, { MessageFormData } from '../components/MessageForm';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import AlertDialog from '../components/AlertDialog';

interface Message {
  id: string;
  name: string;
  content: string;
  content_type: 'text';
  created_at: string;
  is_approved: boolean;
}

const MessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('is_approved', true)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setMessages(data || []);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, []);

  const handleSubmit = async (data: MessageFormData) => {
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          name: data.name,
          content: data.content,
          content_type: 'text',
          is_approved: false
        });
        
      if (error) throw error;
      
      setAlertMessage('Mensagem enviada com sucesso! Ela será exibida após aprovação.');
      setShowAlert(true);
    } catch (error) {
      console.error('Error submitting message:', error);
      throw error;
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <PageHeader 
        title="Mensagens aos Noivos" 
        subtitle="Deixe uma mensagem especial para o casal"
      />
      
      <div className="container mx-auto max-w-4xl mb-10">
        <MessageForm onSubmit={handleSubmit} />
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
        </div>
      ) : (
        <div className="container mx-auto max-w-4xl">
          <h3 className="text-xl font-playfair text-wedding-blue-700 mb-6 text-center">
            Mensagens Recebidas
          </h3>
          
          <div className="space-y-6">
            {messages.map((message, index) => (
              <motion.div
                key={message.id}
                className="bg-white rounded-lg shadow-md p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-wedding-blue-100 rounded-full flex items-center justify-center text-wedding-blue-700 font-medium">
                    {message.name.charAt(0)}
                  </div>
                  <div className="ml-3">
                    <h4 className="font-medium text-wedding-blue-800">{message.name}</h4>
                    <p className="text-xs text-gray-500">
                      {new Date(message.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                
                <p className="text-gray-700">{message.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog
        isOpen={showAlert}
        title="Sucesso"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default MessagesPage;